import { useEffect, useRef } from 'react';
import type { DocumentMeta, Point, ViewState } from '../core/types';
import { documentToScreen, screenToDocument, type CanvasSize } from '../gl/view';
import {
  addGuideTransient,
  beginMetaTransaction,
  commitMetaTransaction,
  moveGuideTransient,
} from '../state/actions';

export const RULER_SIZE = 20;

/** Tick spacings in document pixels, coarse enough to stay readable at any zoom. */
const TICK_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
/** Smallest gap in screen pixels between two labelled ticks. */
const MIN_LABEL_GAP = 56;

function labelStep(zoom: number): number {
  return TICK_STEPS.find((step) => step * zoom >= MIN_LABEL_GAP) ?? TICK_STEPS[TICK_STEPS.length - 1];
}

interface RulersProps {
  doc: DocumentMeta;
  view: ViewState;
  size: CanvasSize;
  devicePixelRatio: number;
  cursor: Point | null;
}

/**
 * The two ruler strips. They sit over the canvas edges rather than taking layout
 * space, so turning them on never changes the document's position on screen.
 * Dragging out of a ruler pulls a new guide, as in Photoshop.
 */
export function Rulers({ doc, view, size, devicePixelRatio, cursor }: RulersProps) {
  const horizontal = useRef<HTMLCanvasElement>(null);
  const vertical = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    drawRuler(horizontal.current, 'horizontal', { doc, view, size, devicePixelRatio, cursor });
    drawRuler(vertical.current, 'vertical', { doc, view, size, devicePixelRatio, cursor });
  }, [doc, view, size, devicePixelRatio, cursor]);

  /** Pulls a guide out of a ruler: the axis is the one the ruler runs along. */
  const startDrag = (axis: 'horizontal' | 'vertical') => (event: React.PointerEvent) => {
    event.preventDefault();
    // Both strips are anchored to the container's top left, so the same offset
    // turns a client position into the canvas-relative one.
    const rect = event.currentTarget.getBoundingClientRect();
    const at = (clientX: number, clientY: number): number => {
      const point = screenToDocument(
        { x: clientX - rect.left, y: clientY - rect.top },
        view,
        size,
      );
      return axis === 'horizontal' ? point.y : point.x;
    };

    beginMetaTransaction();
    const guide = addGuideTransient(axis, at(event.clientX, event.clientY));
    if (!guide) return;

    const onMove = (move: PointerEvent) => {
      moveGuideTransient(guide.id, at(move.clientX, move.clientY));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      commitMetaTransaction('New Guide');
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <>
      <canvas
        className="ruler ruler--horizontal"
        ref={horizontal}
        onPointerDown={startDrag('horizontal')}
      />
      <canvas
        className="ruler ruler--vertical"
        ref={vertical}
        onPointerDown={startDrag('vertical')}
      />
      <div className="ruler__corner" />
    </>
  );
}

function drawRuler(
  canvas: HTMLCanvasElement | null,
  axis: 'horizontal' | 'vertical',
  props: Omit<RulersProps, 'doc'> & { doc: DocumentMeta },
): void {
  if (!canvas) return;
  const { view, size, devicePixelRatio: dpr, cursor, doc } = props;
  const along = axis === 'horizontal' ? size.width : size.height;
  const width = axis === 'horizontal' ? along : RULER_SIZE;
  const height = axis === 'horizontal' ? RULER_SIZE : along;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#2b2b30';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.beginPath();
  if (axis === 'horizontal') {
    ctx.moveTo(0, height - 0.5);
    ctx.lineTo(width, height - 0.5);
  } else {
    ctx.moveTo(width - 0.5, 0);
    ctx.lineTo(width - 0.5, height);
  }
  ctx.stroke();

  const major = labelStep(view.zoom);
  const minor = major / 5;
  const extent = axis === 'horizontal' ? doc.width : doc.height;
  const start = Math.floor(screenToDocument({ x: 0, y: 0 }, view, size)[axis === 'horizontal' ? 'x' : 'y'] / major) * major;
  const end = screenToDocument({ x: size.width, y: size.height }, view, size)[
    axis === 'horizontal' ? 'x' : 'y'
  ];

  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.font = '9px system-ui, sans-serif';
  ctx.textBaseline = 'top';

  for (let value = start; value <= end; value += minor) {
    const point = documentToScreen(
      axis === 'horizontal' ? { x: value, y: 0 } : { x: 0, y: value },
      view,
      size,
    );
    const at = Math.round(axis === 'horizontal' ? point.x : point.y) + 0.5;
    const isMajor = Math.abs(value % major) < minor / 2;
    // Ticks outside the canvas are drawn dimmer, so the document edge reads.
    ctx.globalAlpha = value < 0 || value > extent ? 0.4 : 1;
    const length = isMajor ? RULER_SIZE : RULER_SIZE / 3;
    ctx.beginPath();
    if (axis === 'horizontal') {
      ctx.moveTo(at, RULER_SIZE - length);
      ctx.lineTo(at, RULER_SIZE);
    } else {
      ctx.moveTo(RULER_SIZE - length, at);
      ctx.lineTo(RULER_SIZE, at);
    }
    ctx.stroke();
    if (isMajor) {
      const text = String(Math.round(value));
      if (axis === 'horizontal') ctx.fillText(text, at + 2, 2);
      else {
        ctx.save();
        ctx.translate(2, at - 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(text, 0, 0);
        ctx.restore();
      }
    }
  }
  ctx.globalAlpha = 1;

  if (cursor) {
    const point = documentToScreen(cursor, view, size);
    const at = Math.round(axis === 'horizontal' ? point.x : point.y) + 0.5;
    ctx.strokeStyle = 'rgba(0,180,255,0.9)';
    ctx.beginPath();
    if (axis === 'horizontal') {
      ctx.moveTo(at, 0);
      ctx.lineTo(at, RULER_SIZE);
    } else {
      ctx.moveTo(0, at);
      ctx.lineTo(RULER_SIZE, at);
    }
    ctx.stroke();
  }
}
